import { useMutation } from '@tanstack/react-query'
import { apiClient } from '@/lib/api/client'

/* ────────────────────────────────────────────────────────────────
 * Enums
 * ──────────────────────────────────────────────────────────────── */

export enum EntityType {
  TESTCASE = 'testcase',
  TESTSUITE = 'testsuite',
  PROJECT = 'project',
  TESTPLAN = 'testplan',
  REQUIREMENT = 'requirement',
  EXECUTION = 'execution',
}

export enum ExportFormat {
  CSV = 'csv',
  JSON = 'json',
}

/* ────────────────────────────────────────────────────────────────
 * Helpers
 * ──────────────────────────────────────────────────────────────── */

export function getEntityTypeLabel(type: EntityType): string {
  const map: Record<EntityType, string> = {
    [EntityType.TESTCASE]: 'Test Cases',
    [EntityType.TESTSUITE]: 'Test Suites',
    [EntityType.PROJECT]: 'Projects',
    [EntityType.TESTPLAN]: 'Test Plans',
    [EntityType.REQUIREMENT]: 'Requirements',
    [EntityType.EXECUTION]: 'Test Executions',
  }

  return map[type] ?? type
}

/* ────────────────────────────────────────────────────────────────
 * Response Type
 * ──────────────────────────────────────────────────────────────── */

export interface BulkOperationError {
  index: number
  item_id?: string
  item?: string
  error: string
}

export interface BulkOperationResponse {
  operation: string
  entity_type: EntityType
  total_items: number
  successful: number
  failed: number
  execution_time_ms: number
  errors: BulkOperationError[]
}

/* ────────────────────────────────────────────────────────────────
 * Import CSV
 * POST /api/bulk/import
 * ──────────────────────────────────────────────────────────────── */

interface ImportCSVPayload {
  entity_type: EntityType
  file: File
}

export const useImportCSV = () =>
  useMutation({
    mutationFn: async ({
      entity_type,
      file,
    }: ImportCSVPayload): Promise<BulkOperationResponse> => {
      const formData = new FormData()
      formData.append('entity_type', entity_type)
      formData.append('file', file)

      const { data } = await apiClient.post<BulkOperationResponse>(
        '/api/bulk/import',
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      )

      return data
    },
  })

/* ────────────────────────────────────────────────────────────────
 * Export Entities
 * POST /api/bulk/export
 * ──────────────────────────────────────────────────────────────── */

interface ExportPayload {
  entity_type: EntityType
  format: ExportFormat
}

export const useExportEntities = () =>
  useMutation({
    mutationFn: async ({ entity_type, format }: ExportPayload) => {
      const response = await apiClient.post(
        '/api/bulk/export',
        {
          entity_type,
          format,
        },
        {
          responseType: format === ExportFormat.CSV ? 'blob' : 'json',
        }
      )

      // If CSV → trigger download
      if (format === ExportFormat.CSV) {
        const blob = new Blob([response.data], { type: 'text/csv' })
        const url = window.URL.createObjectURL(blob)

        const link = document.createElement('a')
        link.href = url
        link.download = `${entity_type}-export.csv`
        document.body.appendChild(link)
        link.click()
        link.remove()

        window.URL.revokeObjectURL(url)
      }

      return response.data
    },
  })

/* ────────────────────────────────────────────────────────────────
 * Bulk Delete
 * POST /api/bulk/delete
 * ──────────────────────────────────────────────────────────────── */

interface BulkDeletePayload {
  entity_type: EntityType
  ids: number[]
  hard_delete: boolean
}

export const useBulkDelete = () =>
  useMutation({
    mutationFn: async (
      payload: BulkDeletePayload
    ): Promise<BulkOperationResponse> => {
      const { data } = await apiClient.post<BulkOperationResponse>(
        '/api/bulk/delete',
        payload
      )

      return data
    },
  })

